import { useEffect, useMemo, useRef, useState } from 'react';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import {
    removeCustomQuestionQuery,
    setCustomQuestionQuery,
    useCustomQuestionQuery,
} from '~/lib/Queries';
import * as Toast from '~/lib/Toast';
import useIsAssistantEnabled from '~/lib/useIsAssistantEnabled';
import { ensureObjectArray } from '~/lib/Utils';
import {
    DEFAULT_SECTION_TYPES,
    resolveSettings,
} from '~/lib/CustomQuestions/CustomQuestionApi';

const STARTER = {
    html: `<div class="rating" role="group" aria-label="Rating"></div>
<div class="chosen" aria-live="polite"></div>`,
    css: `.rating { display: flex; gap: 6px; }

.star {
    font-size: 28px;
    line-height: 1;
    cursor: pointer;
    background: none;
    border: 0;
    padding: 0;
    color: #d1d5db;
}

.star.on { color: var(--breely-color); }

.chosen {
    margin-top: 6px;
    font-size: var(--breely-font-size-sm);
    color: var(--breely-color);
}`,
    js: `var rating = document.querySelector('.rating');
var chosen = document.querySelector('.chosen');
var max = breely.settings.maxStars || 5;
var value = breely.value || 0;

function draw() {
    rating.innerHTML = '';

    for (var i = 1; i <= max; i++) {
        var star = document.createElement('button');
        star.type = 'button';
        star.className = 'star' + (i <= value ? ' on' : '');
        star.textContent = '\\u2605';
        star.setAttribute('aria-label', i + ' of ' + max);

        if (!breely.readOnly) {
            star.addEventListener('click', pick(i));
        }

        rating.appendChild(star);
    }

    chosen.textContent = value ? value + ' out of ' + max : '';
}

function pick(i) {
    return function () {
        value = i;
        breely.setValue({ rating: i, max: max });
        breely.setValid(true);
        draw();
    };
}

breely.onValueChange(function (next) {
    value = (next && next.rating) || 0;
    draw();
});

draw();
breely.ready();`,
    settings_schema: JSON.stringify(
        [
            {
                key: 'maxStars',
                type: 'number',
                label: 'Number of stars',
                default: 5,
                min: 1,
                max: 10,
            },
        ],
        null,
        2,
    ),
    answer_schema: JSON.stringify(
        {
            type: 'json',
            summary: '{{rating}} out of {{max}}',
            fields: [{ key: 'rating', label: 'Rating', type: 'number' }],
        },
        null,
        2,
    ),
};

function draftFromSaved(saved) {
    return {
        name: saved.name || '',
        description: saved.description || '',
        icon: saved.icon || 'code',
        html: saved.html || '',
        css: saved.css || '',
        js: saved.js || '',
        flags: saved.flags || {},
        settings_schema: JSON.stringify(saved.settings_schema || [], null, 2),
        answer_schema: JSON.stringify(saved.answer_schema || {}, null, 2),
    };
}

/**
 * The question the assistant saved in its latest turn, if it saved one.
 *  update_custom_question returns the id it wrote to; a refused save returns
 *  an error and no id
 */
function savedQuestionId(messages) {
    const lastUserIdx = messages.findLastIndex(m => m.role === 'user');

    for (const message of messages.slice(lastUserIdx + 1).reverse()) {
        if (message.role !== 'tool' || !message.content) {
            continue;
        }

        for (const toolResult of ensureObjectArray(message.content)) {
            // Tool names arrive prefixed with their feature, ex.
            //  custom_questions-update_custom_question
            const toolName = toolResult.toolName || '';
            if (
                toolName !== 'update_custom_question' &&
                !toolName.endsWith('-update_custom_question')
            ) {
                continue;
            }

            try {
                let result = JSON.parse(toolResult.result);
                if (typeof result?.message === 'string') {
                    result = JSON.parse(result.message);
                }

                if (result?.custom_question_id) {
                    return parseInt(result.custom_question_id);
                }
            } catch {
                // Not a result we can read, so nothing to reload
            }
        }
    }

    return null;
}

/**
 * Everything the custom question editor does, whichever layout it is shown in -
 *  the page (CustomQuestionEditor) or the form designer's modal
 *  (CustomQuestionModalEditor).
 *
 * Saving puts it live: every form already using this question renders the new
 *  code straight away, and it can be added to any form whose section type it
 *  allows, from the Add Element panel.
 *
 * Where it goes from here is up to the caller: onCreated when the assistant
 *  creates the question (so the caller can switch to its id and keep the
 *  conversation), onSaved after Save, onDeleted after a delete.
 *
 * draft is null until the first load.
 */
export default function useCustomQuestionEditor({
    customQuestionId,
    onCreated,
    onSaved,
    onDeleted,
}) {
    const isNew = parseInt(customQuestionId) === 0;

    const { data, refetch } = useCustomQuestionQuery(customQuestionId);
    const isAssistantEnabled = useIsAssistantEnabled();

    const [draft, setDraft] = useState(null);
    const [review, setReview] = useState(null);
    const [previewSettings, setPreviewSettings] = useState({});
    const [activeMode, setActiveMode] = useState('assistant');
    // Set once the assistant has created this question, so what it saved
    //  replaces the starter code when the new question loads
    const loadSavedRef = useRef(false);
    const conversationRef = useRef(null);
    const [activePane, setActivePane] = useState('html');
    const [activeConfig, setActiveConfig] = useState('settings');
    const [showAdditional, setShowAdditional] = useState(false);

    const { save, isSaving, errors } = useBusinessPostApi(
        `/custom-questions/${parseInt(customQuestionId) || 0}`,
    );
    const { save: saveReview } = useBusinessPostApi('/custom-questions/review');
    const { save: saveDelete, isSaving: isDeleting } = useBusinessPostApi(
        `/custom-questions/${parseInt(customQuestionId) || 0}/delete`,
    );

    // Load the saved state into the editor once, or again after the assistant
    //  has created it
    useEffect(() => {
        if (draft && !loadSavedRef.current) {
            return;
        }

        if (isNew) {
            setDraft({
                name: '',
                description: '',
                icon: 'code',
                flags: { rendersAnswer: true, sections: DEFAULT_SECTION_TYPES },
                ...STARTER,
            });
            return;
        }

        if (data?.customQuestion) {
            loadSavedRef.current = false;
            setDraft(draftFromSaved(data.customQuestion));
        }
    }, [data?.customQuestion, isNew, draft]);

    // Open on a greeting rather than an empty chat, which would offer the
    //  general business setup ideas. Handed to the chat as it mounts rather
    //  than set on it afterwards, which silently did nothing whenever the chat
    //  wasn't mounted yet at that moment
    const chatStartingMessages = useMemo(
        () => [
            {
                role: 'assistant',
                content: isNew
                    ? "Describe the question you want and I'll build it for you. There's an example star rating in the preview to the right."
                    : 'What would you like to change about this question?',
            },
        ],
        // Only what it opened on - the assistant creating the question
        //  mid-conversation shouldn't change it
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // The assistant saves straight to the question, so once it has, the
    //  editor and preview show what it wrote
    function onAssistantMessages(messages, isFinal) {
        const savedId = isFinal && savedQuestionId(messages);
        if (!savedId) {
            return;
        }

        if (savedId === parseInt(customQuestionId)) {
            refetch().then(result => {
                if (result.data?.customQuestion) {
                    setDraft(draftFromSaved(result.data.customQuestion));
                }
            });
        } else if (isNew) {
            loadSavedRef.current = true;
            onCreated(savedId);
        }
    }

    // The validator's opinion, half a second after they stop typing. The same
    //  endpoint the assistant uses through preview_custom_question
    useEffect(() => {
        if (!draft) {
            return;
        }

        const timer = setTimeout(() => {
            saveReview({
                html: draft.html,
                css: draft.css,
                js: draft.js,
                settings_schema: draft.settings_schema,
            })
                .then(result => setReview(result?.review))
                .catch(() => {});
        }, 500);

        return () => clearTimeout(timer);
        // Only the code and the schema, not the whole draft - a name change
        //  doesn't need re-reviewing
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft?.html, draft?.css, draft?.js, draft?.settings_schema]);

    // What the preview frame is handed. Parsed from the schema text so the
    //  settings panel beside it reflects what they are typing
    const previewDefinition = useMemo(() => {
        if (!draft) {
            return null;
        }

        let fields = [];
        try {
            const parsed = JSON.parse(draft.settings_schema || '[]');
            fields = Array.isArray(parsed) ? parsed : [];
        } catch {
            fields = review?.fields || [];
        }

        let answerSchema = {};
        try {
            answerSchema = JSON.parse(draft.answer_schema || '{}');
        } catch {
            answerSchema = {};
        }

        return {
            // Draft code changes on every keystroke; the version is what the
            //  frame keys off, so it is tied to the code rather than a row id
            id: parseInt(customQuestionId) || 0,
            version: `draft-${draft.html.length}-${draft.css.length}-${draft.js.length}`,
            name: draft.name || 'Custom question',
            icon: draft.icon,
            html: draft.html,
            css: draft.css,
            js: draft.js,
            fields,
            answerSchema,
            flags: draft.flags || {},
        };
    }, [draft, customQuestionId, review?.fields]);

    const resolvedPreviewSettings = useMemo(
        () => resolveSettings(previewDefinition?.fields || [], previewSettings),
        [previewDefinition?.fields, previewSettings],
    );

    // Which forms this change would go out on, so saving can say so first
    const usedOnForms = data?.usedOnForms?.length ?? 0;

    function set(changes) {
        setDraft({ ...draft, ...changes });
    }

    function saveQuestion() {
        // Saving goes live, so say so before doing it rather than after
        if (
            !isNew &&
            usedOnForms > 0 &&
            !confirm(
                `This replaces what ${usedOnForms === 1 ? 'the form' : `all ${usedOnForms} forms`} using this question shows to your clients. Save it?`,
            )
        ) {
            return Promise.resolve();
        }

        return save({
            ...draft,
            settings_schema: draft.settings_schema,
            answer_schema: draft.answer_schema,
            flags: JSON.stringify(draft.flags || {}),
        }).then(result => {
            setReview(result?.review);
            setCustomQuestionQuery(result?.customQuestion?.id, {
                customQuestion: result?.customQuestion,
            });
            Toast.success(
                isNew
                    ? 'Saved, you can now add this to any of your forms'
                    : 'Saved, your forms are showing this now',
            );

            onSaved(result?.customQuestion, isNew);

            return result;
        });
    }

    function doDelete() {
        if (!confirm(`Delete "${draft.name}"? This cannot be undone.`)) {
            return;
        }

        saveDelete({}).then(() => {
            removeCustomQuestionQuery(customQuestionId);
            Toast.success('Deleted');
            onDeleted();
        });
    }

    const hasBlockingErrors = !!review?.errors?.length;

    return {
        customQuestionId,
        isNew,
        isAssistantEnabled,
        draft,
        set,
        review,
        errors,
        usedOnForms,
        previewDefinition,
        previewSettings,
        setPreviewSettings,
        resolvedPreviewSettings,
        activeMode,
        setActiveMode,
        activePane,
        setActivePane,
        activeConfig,
        setActiveConfig,
        showAdditional,
        setShowAdditional,
        conversationRef,
        chatStartingMessages,
        onAssistantMessages,
        saveQuestion,
        isSaving,
        hasBlockingErrors,
        doDelete,
        isDeleting,
    };
}
